import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { textFontCss } from '../constants';
import { runsOf } from '../engine/richText';
import { placeholderMatches } from '../engine/drawObjects';
import { usePdfEditorStore } from '../state/usePdfEditorStore';
import { TEXT_EDITOR_ATTR, isEditorChrome } from '../ui/prims';

/**
 * The inline text editor.
 *
 * It is a `contenteditable`, not a `<textarea>`, because a text box can be
 * styled per selection: one run bold, another in a different font. A textarea
 * renders a single style, so the caret would sit where its own layout put it
 * and the canvas would draw somewhere else.
 *
 * The DOM is authoritative WHILE TYPING — React does not re-render it on every
 * keystroke, which is what keeps IME composition (Bangla above all) working.
 * Each run is a span tagged with its style, so typing inside one extends that
 * style exactly as the caret suggests, and the marks are read straight back out
 * of the DOM instead of being guessed from a diff. Content is only re-rendered
 * from state when the change came from somewhere else — the options bar, undo.
 */

/** Flat character offset of a DOM position inside `root`. */
function offsetOf(root, node, offset) {
    if (!root.contains(node)) return 0;
    let total = 0;
    const walk = (current) => {
        if (current === node && current.nodeType !== Node.TEXT_NODE) {
            // A position between children: count everything before it.
            for (let i = 0; i < offset; i += 1) total += lengthOf(current.childNodes[i]);
            return true;
        }
        if (current.nodeType === Node.TEXT_NODE) {
            if (current === node) { total += offset; return true; }
            total += current.data.length;
            return false;
        }
        if (current.nodeName === 'BR') { total += 1; return false; }
        for (const child of current.childNodes) if (walk(child)) return true;
        return false;
    };
    walk(root);
    return total;
}

function lengthOf(node) {
    if (!node) return 0;
    if (node.nodeType === Node.TEXT_NODE) return node.data.length;
    if (node.nodeName === 'BR') return 1;
    return [...node.childNodes].reduce((sum, child) => sum + lengthOf(child), 0);
}

/** The DOM position for a flat character offset — the inverse of `offsetOf`. */
function positionAt(root, index) {
    let remaining = index;
    let fallback = { node: root, offset: 0 };
    const walk = (current) => {
        if (current.nodeType === Node.TEXT_NODE) {
            if (remaining <= current.data.length) return { node: current, offset: remaining };
            remaining -= current.data.length;
            fallback = { node: current, offset: current.data.length };
            return null;
        }
        if (current.nodeName === 'BR') {
            if (remaining === 0) return { node: current.parentNode, offset: [...current.parentNode.childNodes].indexOf(current) };
            remaining -= 1;
            return null;
        }
        for (const child of current.childNodes) {
            const hit = walk(child);
            if (hit) return hit;
        }
        return null;
    };
    return walk(root) || fallback;
}

/** Text and per-character style index, read out of the rendered spans. */
function readContent(root) {
    let text = '';
    const owners = [];
    const walk = (node, styleIndex) => {
        if (node.nodeType === Node.TEXT_NODE) {
            text += node.data;
            for (let i = 0; i < node.data.length; i += 1) owners.push(styleIndex);
            return;
        }
        if (node.nodeName === 'BR') { text += '\n'; owners.push(styleIndex); return; }
        // A block the browser made (Chrome wraps pasted or Enter-split lines
        // in <div>s) starts a new line — reading it as plain inline text ran
        // every pasted paragraph into one line once the box closed.
        if ((node.nodeName === 'DIV' || node.nodeName === 'P') && text && !text.endsWith('\n')) {
            text += '\n';
            owners.push(styleIndex);
        }
        const own = node.dataset?.style;
        const next = own === undefined ? styleIndex : Number(own);
        for (const child of node.childNodes) walk(child, next);
    };
    for (const child of root.childNodes) walk(child, -1);
    return { text, owners };
}

/** Put the selection back after the spans were rebuilt under it. */
function select(root, { start, end }, length) {
    const from = positionAt(root, Math.min(start, length));
    const to = positionAt(root, Math.min(end, length));
    const range = document.createRange();
    range.setStart(from.node, from.offset);
    range.setEnd(to.node, to.offset);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
}

/** Marks rebuilt from the style each character was typed into. */
function marksFrom(owners, styles) {
    const out = [];
    let at = 0;
    while (at < owners.length) {
        const index = owners[at];
        let end = at + 1;
        while (end < owners.length && owners[end] === index) end += 1;
        const style = index >= 0 ? styles[index] : null;
        if (style && Object.keys(style).length) out.push({ start: at, end, style });
        at = end;
    }
    return out;
}

export const cssOf = (style, scale) => ({
    fontFamily: textFontCss(style),
    fontSize: `${style.fontSize * scale}px`,
    fontWeight: style.bold ? 700 : 400,
    fontStyle: style.italic ? 'italic' : 'normal',
    // Both can be on at once, which is why this is built rather than picked.
    textDecoration: [style.underline && 'underline', style.strike && 'line-through']
        .filter(Boolean).join(' ') || 'none',
    color: style.color,
    ...(style.outline ? { WebkitTextStroke: `${Math.max(0.6, style.fontSize * 0.045 * scale)}px #1C1815` } : {}),
    ...(style.shadow
        ? { textShadow: `${style.fontSize * 0.06 * scale}px ${style.fontSize * 0.08 * scale}px ${style.fontSize * 0.14 * scale}px rgba(28,24,21,0.45)` }
        : {}),
});

/** No formatting is no formatting, however it is spelled (null, undefined, []). */
const sameMarks = (a, b) => a === b || (!a?.length && !b?.length);

/**
 * Where the caret sits in the box: at the very start / end of its text, and on
 * its first / last visual line — what a table cell needs to hand the arrow keys
 * on to the cell beside it, as Word and Google Docs do.
 */
function caretEdges(root, selection, length) {
    const edges = { atStart: false, atEnd: false, firstLine: true, lastLine: true, collapsed: true };
    const sel = window.getSelection();
    if (!root || !sel?.rangeCount) return edges;
    edges.collapsed = sel.isCollapsed;
    edges.atStart = selection.start === 0 && selection.end === 0;
    edges.atEnd = selection.start === length && selection.end === length;
    const caret = sel.getRangeAt(0).getClientRects()[0];
    const all = document.createRange();
    all.selectNodeContents(root);
    const lines = [...all.getClientRects()];
    if (!caret || !lines.length) return edges;
    edges.firstLine = caret.top <= lines[0].top + 2;
    edges.lastLine = caret.bottom >= lines[lines.length - 1].bottom - 2;
    return edges;
}

/** Character ranges of the "[placeholder]" variables in a flat text. */
function placeholderRanges(text, plainBrackets = false) {
    return placeholderMatches(text, plainBrackets).map((m) => [m.index, m.index + m[0].length]);
}

/** The DOM position under a viewport point (Chrome/Safari and Firefox spell it differently). */
function caretFromPoint(x, y) {
    if (document.caretPositionFromPoint) {
        const p = document.caretPositionFromPoint(x, y);
        return p ? { node: p.offsetNode, offset: p.offset } : null;
    }
    if (document.caretRangeFromPoint) {
        const r = document.caretRangeFromPoint(x, y);
        return r ? { node: r.startContainer, offset: r.startOffset } : null;
    }
    return null;
}

/** Name of the CSS highlight that paints the placeholder chips (see global.scss). */
const PH_HIGHLIGHT = 'pdf-ph';

export default function TextEditor({
    object,
    styleObject,
    scale,
    className,
    style,
    onChange,
    onFinish,
    onSelection,
    /** Document mode: [placeholders] are chips — a click selects the whole variable so typing replaces it. */
    selectPlaceholders = false,
    /**
     * Offered the pasted text first; returns true when it handled the paste
     * itself (document mode pasting whole paragraphs with their formatting).
     */
    onPasteText,
    /**
     * Offered Tab and the arrow keys first, with where the caret is
     * (`caretEdges`); returns true when it moved the editing elsewhere — a
     * table cell stepping to the next cell.
     */
    onEdgeKey,
}) {
    const language = usePdfEditorStore((s) => s.language);
    const ref = useRef(null);
    const stylesRef = useRef([]);
    const mineRef = useRef({ text: null, marks: null });
    const paintedRef = useRef(null);
    const selectionRef = useRef({ start: 0, end: 0 });

    const base = styleObject || object;

    // Everything `cssOf` paints onto a span. A box-level restyle — Bold, a size,
    // a colour, a font — leaves the text and the marks untouched, so this is the
    // only thing that can tell the spans they are out of date.
    const painted = [
        base.fontStack, base.fontFamily, base.fontSize, base.bold,
        base.italic, base.underline, base.strike, base.outline, base.shadow, base.color, scale,
    ].join('|');

    /**
     * Paint the "[placeholder]" chips with the CSS Custom Highlight API: it colours ranges
     * without touching the DOM, so typing, IME composition and the caret are left alone.
     * Browsers without it simply show no chips.
     */
    const paintPlaceholders = useCallback(() => {
        if (!selectPlaceholders || typeof CSS === 'undefined' || !CSS.highlights || typeof Highlight === 'undefined') return;
        const el = ref.current;
        if (!el) return;
        const ranges = placeholderRanges(readContent(el).text, object.plainBrackets).map(([a, b]) => {
            const from = positionAt(el, a);
            const to = positionAt(el, b);
            const range = document.createRange();
            range.setStart(from.node, from.offset);
            range.setEnd(to.node, to.offset);
            return range;
        });
        CSS.highlights.set(PH_HIGHLIGHT, new Highlight(...ranges));
    }, [selectPlaceholders, object.plainBrackets]);

    useEffect(() => () => {
        if (typeof CSS !== 'undefined' && CSS.highlights) CSS.highlights.delete(PH_HIGHLIGHT);
    }, []);

    /** If the caret sits inside a [placeholder], select the whole variable. Returns whether it did. */
    const selectTokenAtCaret = useCallback(() => {
        const el = ref.current;
        const selection = window.getSelection();
        if (!el || !selection?.rangeCount || !selection.isCollapsed || !el.contains(selection.anchorNode)) return false;
        const { text } = readContent(el);
        const at = offsetOf(el, selection.anchorNode, selection.anchorOffset);
        const token = placeholderRanges(text, object.plainBrackets).find(([a, b]) => at >= a && at <= b);
        if (!token) return false;
        select(el, { start: token[0], end: token[1] }, text.length);
        return true;
    }, [object.plainBrackets]);

    /** Rebuild the spans from state, keeping the caret where it was. */
    const render = useCallback((caret) => {
        const el = ref.current;
        if (!el) return;
        const runs = runsOf({ ...base, text: object.text, marks: object.marks });
        // Only the difference from the box's own style belongs on a run.
        stylesRef.current = runs.map((run) => {
            const diff = {};
            for (const key of Object.keys(run.style)) {
                if (run.style[key] !== base[key]) diff[key] = run.style[key];
            }
            return diff;
        });

        el.textContent = '';
        runs.forEach((run, index) => {
            const span = document.createElement('span');
            span.dataset.style = String(index);
            Object.assign(span.style, cssOf(run.style, scale));
            // A trailing newline needs a <br> of its own or the browser eats it.
            span.textContent = run.text;
            el.appendChild(span);
        });
        if (!runs.length || !object.text) el.appendChild(document.createElement('br'));

        if (caret && document.activeElement === el) select(el, caret, object.text?.length ?? 0);
        paintPlaceholders();
    }, [base, object.text, object.marks, scale, paintPlaceholders]);

    // Fill the box when it opens, and again whenever it changed somewhere else
    // (a style applied from the ribbon, an undo, a zoom).
    useLayoutEffect(() => {
        // Our own keystrokes are already on screen and rebuilding them would
        // drop the caret — but only the typing is already on screen. A restyle
        // has to repaint even though the text it applies to did not move.
        const mine = object.text === mineRef.current.text && sameMarks(object.marks, mineRef.current.marks);
        if (mine && painted === paintedRef.current) return;
        mineRef.current = { text: object.text, marks: object.marks };
        paintedRef.current = painted;
        // The whole selection comes back, not just a caret: styling a word and
        // then styling it again (bold, then italic) has to keep it highlighted.
        render(selectionRef.current);
    }, [object.text, object.marks, render, painted]);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const end = object.text?.length ?? 0;
        el.focus({ preventScroll: true });
        // Opened by a click (document mode): put the caret where the click landed, and if that
        // is a [placeholder], select the whole variable so typing replaces it. An exact
        // character (a step into the next table cell) is honoured everywhere.
        const editAt = usePdfEditorStore.getState().editAt;
        const at = selectPlaceholders || typeof editAt?.offset === 'number' ? editAt : null;
        let caret = { start: end, end };
        if (at && typeof at.offset === 'number') {
            // Opened at a known character (after a replace across paragraphs).
            const offset = Math.max(0, Math.min(end, at.offset));
            caret = { start: offset, end: offset };
        } else if (at) {
            const hit = caretFromPoint(at.x, at.y);
            if (hit && el.contains(hit.node)) {
                const offset = offsetOf(el, hit.node, hit.offset);
                caret = { start: offset, end: offset };
            }
            const token = placeholderRanges(object.text, object.plainBrackets).find(([a, b]) => caret.start >= a && caret.start <= b);
            if (token) caret = { start: token[0], end: token[1] };
        }
        select(el, caret, end);
        selectionRef.current = caret;
        return undefined;
        // Only when a different box is opened.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [object.id]);

    const readSelection = useCallback(() => {
        const el = ref.current;
        const selection = window.getSelection();
        if (!el || !selection?.rangeCount || !el.contains(selection.anchorNode)) return;
        const a = offsetOf(el, selection.anchorNode, selection.anchorOffset);
        const b = offsetOf(el, selection.focusNode, selection.focusOffset);
        const next = { start: Math.min(a, b), end: Math.max(a, b) };
        selectionRef.current = next;
        onSelection?.(next);
    }, [onSelection]);

    useEffect(() => {
        document.addEventListener('selectionchange', readSelection);
        return () => document.removeEventListener('selectionchange', readSelection);
    }, [readSelection]);

    const handleInput = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const { text, owners } = readContent(el);
        const marks = marksFrom(owners, stylesRef.current);
        // Remember what we emitted so the render effect knows this change is
        // already on screen and must not rebuild the DOM under the caret.
        mineRef.current = { text, marks };
        onChange({ text, marks });
        paintPlaceholders();
    }, [onChange, paintPlaceholders]);

    return (
        <div
            ref={ref}
            {...{ [TEXT_EDITOR_ATTR]: '' }}
            contentEditable
            suppressContentEditableWarning
            spellCheck={false}
            lang={language}
            role="textbox"
            tabIndex={0}
            className={className}
            // The box's own style sits on the ROOT, not only on the run spans.
            // Typing into an empty box puts the characters in a bare text node
            // beside the span rather than inside it, and a bare node inherits —
            // so without this the first thing anyone types came out in the page's
            // black however the colour was set. A run with a mark still overrides
            // it from its own span.
            style={{
                ...cssOf(base, scale),
                ...style,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
            }}
            onInput={handleInput}
            onFocus={() => {
                // The options bar hands the caret back after a click; put it
                // where it was rather than at the start of the box.
                const el = ref.current;
                if (el) select(el, selectionRef.current, object.text?.length ?? 0);
            }}
            onKeyUp={readSelection}
            onMouseUp={() => {
                // A click on a [placeholder] selects the whole variable (the old editor's chip).
                if (selectPlaceholders) selectTokenAtCaret();
                readSelection();
            }}
            onBlur={(e) => {
                if (isEditorChrome(e.relatedTarget)) return;
                onFinish();
            }}
            onKeyDown={(e) => {
                if (onEdgeKey && (e.key === 'Tab' || /^Arrow/.test(e.key)) && !e.isComposing) {
                    const el = ref.current;
                    const length = readContent(el).text.length;
                    // Read now, not from the last selectionchange (which lands after the key).
                    const sel = window.getSelection();
                    const here = sel?.rangeCount && el.contains(sel.anchorNode)
                        ? (() => {
                            const a = offsetOf(el, sel.anchorNode, sel.anchorOffset);
                            const b = offsetOf(el, sel.focusNode, sel.focusOffset);
                            return { start: Math.min(a, b), end: Math.max(a, b) };
                        })()
                        : selectionRef.current;
                    if (onEdgeKey(e, caretEdges(el, here, length))) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                    }
                }
                if (e.key === 'Escape') { e.preventDefault(); onFinish(); }
                if (e.key === 'Enter') {
                    // Browsers otherwise split the box into <div>s, which the
                    // reader would have to un-nest on every keystroke.
                    e.preventDefault();
                    document.execCommand('insertLineBreak');
                }
                e.stopPropagation();
            }}
            onPaste={(e) => {
                // Plain text only: pasted markup would arrive with styles the
                // object model knows nothing about.
                e.preventDefault();
                const text = (e.clipboardData?.getData('text/plain') ?? '').replace(/\r\n?/g, '\n');
                if (onPasteText?.(text)) return;
                // Line by line, with real line breaks between: `insertText`
                // given a newline makes block <div>s instead of the <br> this
                // editor reads back as one.
                text.split('\n').forEach((line, i) => {
                    if (i > 0) document.execCommand('insertLineBreak');
                    if (line) document.execCommand('insertText', false, line);
                });
            }}
        />
    );
}
